import React, { useEffect, useRef, useState } from 'react';
import { usePickBrands, BrandPick } from './TagBrandBits';
import { notesApi } from '@/lib/notesApi';
import { fileToCappedDataUrl } from '@/lib/imageResize';
import { getNotesModuleCaps } from '@/lib/attachmentCaps';
import { toast } from 'sonner';
import { extractError } from '@/lib/errors';
import { X, Plus, Trash2, Loader2, Image as ImageIcon, Save } from 'lucide-react';
import { tagThemeClass } from './tagTheme';
import { ChargesEditor, chargeRows, chargesBody } from './TagExtraCharges';

const MATERIAL_TYPES = [
  { v: 'diamond', l: 'Diamond' },
  { v: 'color_stone', l: 'Colour Stone' },
  { v: 'pearl', l: 'Pearl' },
  { v: 'other', l: 'Other' },
];

const INPUT = 'w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-1 focus-visible:ring-offset-background';
const LABEL = 'mb-1 block text-[length:calc(var(--fs,1)*0.6875rem)] font-semibold uppercase tracking-wide text-muted-foreground';

/**
 * TagManualTagDialog — create or edit a manual tag (tag_only). Uses the same
 * core fields as a portal jewellery tag. In-scope inline modal so the neutral
 * `.tag-scope` tokens apply (radix portals would escape the scope).
 */
export function TagManualTagDialog({ open, mode = 'create', initial = null, masters, onClose, onSaved }) {
  const fileRef = useRef(null);
  const [image, setImage] = useState('');
  const [jewNo, setJewNo] = useState('');
  const [title, setTitle] = useState('');
  const [jewType, setJewType] = useState('');
  const [smithId, setSmithId] = useState('');
  const [smiths, setSmiths] = useState([]);
  const pickBrands = usePickBrands();
  const [brandId, setBrandId] = useState('');
  const [custId, setCustId] = useState('');
  const [custs, setCusts] = useState([]);
  const [purityName, setPurityName] = useState('');
  const [gross, setGross] = useState('');
  const [rows, setRows] = useState([]);
  const [charges, setCharges] = useState([]);
  const [displayNames, setDisplayNames] = useState([]);
  useEffect(() => { if (open) notesApi.get('/notes/tag-workspace/goldsmiths').then(({ data }) => setSmiths(data || [])).catch(() => {}); }, [open]);
  useEffect(() => { if (open) notesApi.get('/notes/tag-workspace/customers').then(({ data }) => setCusts(data || [])).catch(() => {}); }, [open]);
  useEffect(() => { if (open) notesApi.get('/notes/tag-workspace/tag-display-names').then(({ data }) => setDisplayNames(data || [])).catch(() => {}); }, [open]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const purities = masters?.gold_purities || [];
  const types = masters?.jewellery_types || [];
  const classes = masters?.classifications || [];
  const masterTagName = (row) => {
    const code = (row.classification || '').trim().toUpperCase();
    const hit = classes.find((c) => c.code === code && c.material_type === row.material_type) || classes.find((c) => c.code === code);
    return hit?.tag_display_name || '';
  };
  const printsAs = (row) => (row.tag_display_name || '').trim() || masterTagName(row)
    || (row.classification || '').replace(/chamka/gi, '').replace(/[\s\-_/]+$|^[\s\-_/]+/g, '').replace(/\s*-\s*-\s*/g, '-').trim();

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && initial) {
      setImage(initial.image || '');
      setJewNo(initial.jewellery_no || '');
      setTitle(initial.title || '');
      setJewType(initial.jewellery_type || '');
      setSmithId(initial.goldsmith_id || '');
      setCustId(initial.customer_id || '');
      setPurityName(initial.gold_purity_name || '');
      const g0 = initial.alter_base ? initial.alter_base.gross : initial.gross_weight_grams; // before alterations (those stay on top)
      setGross(g0 != null ? String(g0) : '');
      const snap = Array.isArray(initial.rm_recorded) ? initial.rm_recorded : Array.isArray(initial.alter_base?.rm) ? initial.alter_base.rm : Array.isArray(initial.rm_consumed_snapshot) ? initial.rm_consumed_snapshot : []; // recorded carats, before any uplift
      setRows(snap.map((r) => ({
        classification: r.classification_raw || r.classification_schema || r.classification || '',
        note: r.issue_note || '',
        tag_display_name: r.tag_display_name || '',
        shape: r.shape || '', size: r.size || '', sieves: r.sieves || '',
        carats: r.carats != null ? String(r.carats) : '',
        pieces: r.pieces != null ? String(r.pieces) : '',
        material_type: r.material_type || 'other',
      })));
      setCharges(chargeRows(initial.extra_charges));
    } else {
      setCharges([]);
      setImage(''); setJewNo(''); setTitle(''); setJewType(''); setPurityName(''); setGross(''); setSmithId(''); setCustId('');
      setRows([{ classification: '', carats: '', pieces: '', material_type: 'diamond' }]);
    }
  }, [open, mode, initial]);

  const onPickImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try { const caps = await getNotesModuleCaps(); const url = await fileToCappedDataUrl(file, caps?.erp_kb); setImage(url); }
    catch (e) { toast.error('Could not load that image.'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const addRow = () => setRows((r) => [...r, { classification: '', carats: '', pieces: '', material_type: 'diamond' }]);
  const removeRow = (i) => setRows((r) => r.filter((_, idx) => idx !== i));
  const setRow = (i, k, val) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, [k]: val } : row)));

  // System formula: 5 ct = 1 g — net = gross − (total raw-material carats / 5).
  // Carats count at 2 decimals (as shown on tags) so gross - stones = net exactly.
  const totalCt = rows.reduce((sum, r) => sum + ((r.classification || '').trim() ? Math.round((parseFloat(r.carats || '0') || 0) * 100 + 1e-7) / 100 : 0), 0);
  const netCalc = +((parseFloat(gross || '0') || 0) - totalCt / 5).toFixed(3);
  const netNegative = netCalc < 0;

  const canSave = jewNo.trim().length > 0 && !busy && !netNegative;

  const submit = async () => {
    if (!jewNo.trim()) { toast.error('Jewellery number is required.'); return; }
    if (netNegative) { toast.error('Net weight became negative. Check stone carats vs gross weight.'); return; }
    setBusy(true);
    const p = purities.find((x) => x.name === purityName);
    const materials = rows
      .filter((r) => (r.classification || '').trim())
      .map((r) => ({
        classification: r.classification.trim(),
        carats: parseFloat(r.carats || '0') || 0,
        pieces: parseInt(r.pieces || '0', 10) || 0,
        material_type: r.material_type || 'other',
        note: (r.note || '').trim(),
        tag_display_name: (r.tag_display_name || '').trim(),
        shape: (r.shape || '').trim(),
        size: (r.size || '').trim(),
        sieves: (r.sieves || '').trim(),
      }));
    const payload = {
      jewellery_no: jewNo.trim(),
      ...(mode === 'edit' ? {} : { brand_id: brandId }),
      title: title.trim(),
      jewellery_type: jewType.trim(),
      goldsmith_id: smithId,
      customer_id: custId,
      gold_purity_name: purityName || null,
      gold_purity_percent: p ? p.percent : null,
      gross_weight_grams: parseFloat(gross || '0') || 0,
      net_weight_grams: netCalc,
      image: image || '',
      materials,
      extra_charges: chargesBody(charges),
    };
    // Cards carry a small preview; keep the stored full photo unless the user changed it.
    if (mode === 'edit' && initial && image === (initial.image || '')) delete payload.image;
    try {
      if (mode === 'edit' && initial) {
        await notesApi.patch(`/notes/tag-workspace/manual/${initial.id}`, payload);
        toast.success('Tag updated.');
      } else {
        await notesApi.post('/notes/tag-workspace/manual', payload);
        toast.success('Manual tag created.');
      }
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(extractError(e, 'Could not save the tag.'));
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  return (
    <div className={`tag-scope ${tagThemeClass()} fixed inset-0 z-[200] flex items-center justify-center p-4`} data-testid="tagws-manual-tag-dialog">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]" onClick={() => !busy && onClose?.()} />
      <div className="relative z-10 max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-border bg-card shadow-xl">
        <div className="flex items-start justify-between border-b border-border px-5 py-4">
          <div>
            <h3 className="text-lg font-semibold tracking-tight text-foreground">{mode === 'edit' ? 'Edit manual tag' : 'Create manual tag'}</h3>
            <p className="text-xs text-muted-foreground">Enter the same core details as a jewellery tag.</p>
          </div>
          <button onClick={() => !busy && onClose?.()} aria-label="Close" className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
        </div>

        <div className="grid grid-cols-1 gap-4 p-5 md:grid-cols-2">
          <div className="space-y-3">
            <div>
              <span className={LABEL}>Image</span>
              <div className="flex items-center gap-3">
                <div className="grid h-24 w-24 shrink-0 place-items-center overflow-hidden rounded-lg border border-border bg-secondary">
                  {image ? <img src={image} alt="preview" className="h-full w-full object-contain p-1" /> : <ImageIcon size={22} className="text-muted-foreground" />}
                </div>
                <div className="flex flex-col gap-1.5">
                  <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-3 py-1.5 text-sm text-foreground hover:bg-secondary">
                    {uploading ? <Loader2 size={14} className="animate-spin" /> : null} {image ? 'Replace' : 'Upload'} image
                  </button>
                  {image && <button type="button" onClick={() => setImage('')} className="text-left text-xs text-muted-foreground hover:text-destructive">Remove</button>}
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="tagws-manual-tag-image-input" onChange={(e) => onPickImage(e.target.files?.[0])} />
                </div>
              </div>
            </div>
            <div>
              <span className={LABEL}>Jewellery number *</span>
              <input value={jewNo} onChange={(e) => setJewNo(e.target.value)} placeholder="e.g. RNG-1001" className={`${INPUT} font-mono`} data-testid="tagws-manual-tag-number-input" />
            </div>
            <div>
              <span className={LABEL}>Title</span>
              <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Short description" className={INPUT} data-testid="tagws-manual-tag-title-input" />
            </div>
            <div>
              <span className={LABEL}>Type of jewellery</span>
              <input value={jewType} onChange={(e) => setJewType(e.target.value)} list="tagws-manual-type-list" placeholder="Pick or type a type…" className={INPUT} data-testid="tagws-manual-tag-type-select" />
              <datalist id="tagws-manual-type-list">{types.map((t) => <option key={t.id} value={t.name} />)}</datalist>
            </div>
            {mode === 'edit' ? null : <BrandPick brands={pickBrands} value={brandId} onChange={setBrandId} testid="tagws-manual-tag-brand-select" />}
            <div>
              <span className={LABEL}>Goldsmith</span>
              <select value={smithId} onChange={(e) => setSmithId(e.target.value)} className={INPUT} disabled={!smiths.length} data-testid="tagws-manual-tag-goldsmith-select">
                <option value="">{smiths.length ? 'Select goldsmith…' : 'No goldsmiths in the main portal'}</option>
                {smiths.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            </div>
            <div>
              <span className={LABEL}>Customer</span>
              <select value={custId} onChange={(e) => setCustId(e.target.value)} className={INPUT} disabled={!custs.length} data-testid="tagws-manual-tag-customer-select">
                <option value="">{custs.length ? 'Select customer…' : 'No customers in the main portal'}</option>
                {custs.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <span className={LABEL}>Gold purity</span>
              <select value={purityName} onChange={(e) => setPurityName(e.target.value)} className={INPUT} data-testid="tagws-manual-tag-purity-select">
                <option value="">Select purity…</option>
                {purities.map((p) => <option key={p.id} value={p.name}>{p.name}{p.percent ? ` (${p.percent}%)` : ''}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <span className={LABEL}>Gross weight (g)</span>
                <input type="number" step="0.001" min="0" value={gross} onChange={(e) => setGross(e.target.value)} className={`${INPUT} font-mono`} data-testid="tagws-manual-tag-gross-weight-input" />
              </div>
              <div>
                <span className={LABEL}>Net weight (g)</span>
                <input type="text" readOnly value={gross || totalCt ? netCalc.toFixed(3) : ''} placeholder="Auto" className={`${INPUT} cursor-not-allowed bg-secondary font-mono ${netNegative ? 'text-destructive' : ''}`} data-testid="tagws-manual-tag-net-weight-input" />
              </div>
            </div>
            <p className="text-[length:calc(var(--fs,1)*0.6875rem)] text-muted-foreground" data-testid="tagws-manual-tag-net-formula">
              Net = Gross − (Total carats {totalCt.toFixed(2)} ct ÷ 5) = {netCalc.toFixed(3)} g
            </p>
            {netNegative && <p className="text-[length:calc(var(--fs,1)*0.6875rem)] text-destructive" data-testid="tagws-manual-tag-net-negative">Net weight can't be negative — check stone carats vs gross weight.</p>}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className={`${LABEL} mb-0`}>Raw materials</span>
              <button type="button" onClick={addRow} data-testid="tagws-raw-material-add-row-button" className="inline-flex items-center gap-1 rounded-lg border border-border bg-background px-2.5 py-1 text-xs text-foreground hover:bg-secondary"><Plus size={13} /> Add row</button>
            </div>
            {['diamond', 'color_stone', 'pearl', 'other'].map((mt) => (
              <datalist key={mt} id={`tagws-class-list-${mt}`}>
                {classes.filter((c) => mt === 'other' || c.material_type === mt).map((c) => (
                  <option key={`${c.material_type}-${c.code}`} value={c.code}>{c.tag_display_name ? `Prints as ${c.tag_display_name}` : ''}</option>
                ))}
              </datalist>
            ))}
            <div className="space-y-2">
              {rows.length === 0 && <div className="rounded-lg border border-dashed border-border p-3 text-center text-xs text-muted-foreground">No materials. Add rows for diamonds, colour stones or pearls.</div>}
              {rows.map((row, i) => (
                <div key={i} className="space-y-2 rounded-lg border border-border bg-secondary/40 p-2.5" data-testid="tagws-raw-material-row">
                  <div className="flex items-center gap-2">
                    <input value={row.classification} onChange={(e) => setRow(i, 'classification', e.target.value)} placeholder="Classification (e.g. VVS Round)" list={`tagws-class-list-${row.material_type}`} className={`${INPUT} py-1.5`} data-testid="tagws-raw-material-classification-input" />
                    <button type="button" onClick={() => removeRow(i)} aria-label="Remove row" data-testid="tagws-raw-material-remove-row-button" className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-muted-foreground hover:bg-background hover:text-destructive"><Trash2 size={14} /></button>
                  </div>
                  <div className="grid grid-cols-[1.4fr_1fr_0.8fr] gap-2">
                    <select value={row.material_type} onChange={(e) => setRow(i, 'material_type', e.target.value)} className={`${INPUT} py-1.5`}>
                      {MATERIAL_TYPES.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
                    </select>
                    <input type="number" step="0.001" min="0" value={row.carats} onChange={(e) => setRow(i, 'carats', e.target.value)} placeholder="Carats" className={`${INPUT} py-1.5 font-mono`} />
                    <input type="number" step="1" min="0" value={row.pieces} onChange={(e) => setRow(i, 'pieces', e.target.value)} placeholder="Pcs" className={`${INPUT} py-1.5 font-mono`} />
                  </div>
                  <div className="grid grid-cols-[1fr_auto] items-center gap-2">
                    <input value={row.tag_display_name || ''} onChange={(e) => setRow(i, 'tag_display_name', e.target.value)} maxLength={60} list="tagws-display-names"
                      placeholder={masterTagName(row) ? `Tag display name (default: ${masterTagName(row)})` : 'Tag display name (optional)'}
                      className={`${INPUT} py-1.5 text-xs`} data-testid="tagws-raw-material-tag-name-input" />
                    {(row.classification || '').trim() ? (
                      <span className="max-w-[180px] truncate rounded-md bg-[hsl(var(--accent))] px-2 py-1 text-[length:calc(var(--fs,1)*0.6875rem)] text-foreground" title="What prints on the tag" data-testid="tagws-raw-material-prints-as">
                        {['POLKI-DAK', 'POLKI-SLV'].includes((row.classification || '').trim().toUpperCase())
                          ? <>Added to <b>net weight</b></>
                          : <>Prints as: <b>{printsAs(row) || '—'}</b></>}
                      </span>
                    ) : null}
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <input value={row.shape || ''} onChange={(e) => setRow(i, 'shape', e.target.value)} placeholder="Shape" maxLength={60} className={`${INPUT} py-1.5 text-xs`} data-testid="tagws-raw-material-shape-input" />
                    <input value={row.size || ''} onChange={(e) => setRow(i, 'size', e.target.value)} placeholder="Size" maxLength={60} className={`${INPUT} py-1.5 text-xs`} data-testid="tagws-raw-material-size-input" />
                    <input value={row.sieves || ''} onChange={(e) => setRow(i, 'sieves', e.target.value)} placeholder="Sieve (e.g. +2-6.1/2)" maxLength={60} className={`${INPUT} py-1.5 font-mono text-xs`} data-testid="tagws-raw-material-sieve-input" />
                  </div>
                  <input value={row.note || ''} onChange={(e) => setRow(i, 'note', e.target.value)} placeholder="Note (optional — shown on Expanded tags)" maxLength={200} className={`${INPUT} py-1.5 text-xs`} data-testid="tagws-raw-material-note-input" />
                </div>
              ))}
            </div>
          </div>

          <datalist id="tagws-display-names" data-testid="tagws-display-names-list">{displayNames.map((n) => <option key={n} value={n} />)}</datalist>
          <div className="space-y-2" data-testid="tagws-manual-tag-charges">
            <span className={`${LABEL} mb-0`}>Extra charges <span className="font-normal normal-case text-muted-foreground">(not printed on the tag — added to priced Excel / PDF)</span></span>
            <ChargesEditor rows={charges} onChange={setCharges} inputClass={`${INPUT} py-1.5`} />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border px-5 py-4">
          <button onClick={() => !busy && onClose?.()} data-testid="tagws-manual-tag-cancel-button" className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-4 py-2 text-sm text-foreground hover:bg-secondary">Cancel</button>
          <button onClick={submit} disabled={!canSave} data-testid="tagws-manual-tag-save-button" className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-colors duration-150 hover:opacity-90 disabled:opacity-50 active:scale-[0.98]">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} {mode === 'edit' ? 'Save changes' : 'Create tag'}
          </button>
        </div>
      </div>
    </div>
  );
}
